import { AISummaryContent } from '@app/src/pages/Questionnaire/Accessor/Report/AiScreening/AISummaryContent'
import {
  AnswerEvidenceDetails,
  hasEvidenceDetails,
} from '@app/src/pages/Questionnaire/Accessor/Report/AiScreening/AnswerEvidenceDetails'
import type { AnswerPanelTab } from '@app/src/pages/Questionnaire/Accessor/Report/AiScreening/AnswerPanelTabs'
import { AnswerPanelTabs } from '@app/src/pages/Questionnaire/Accessor/Report/AiScreening/AnswerPanelTabs'
import { AnswerSourcesList } from '@app/src/pages/Questionnaire/Accessor/Report/AiScreening/AnswerSourcesList'
import { getFaviconUrl } from '@app/src/pages/Questionnaire/Accessor/Report/AiScreening/favicon'
import type { ScrapedAnswer } from '@app/src/types/resourceExplorer'
import { ConfidenceLevel } from '@app/src/types/resourceExplorer'
import { insertIf } from '@app/src/utils/helpersTs'
import { Avatar, AvatarGroup, Box, Divider, Stack } from '@mui/material'
import React, { useState } from 'react'
import { useIntl } from 'react-intl'

interface AnswerSourcesPanelProps {
  scrapedAnswer?: ScrapedAnswer
}

enum AnswerPanelTabType {
  AISummary = 'ai-summary',
  Sources = 'sources',
  Confidence = 'confidence',
}

const confidenceColor: Record<ConfidenceLevel, string> = {
  [ConfidenceLevel.High]: 'secondary.main',
  [ConfidenceLevel.Medium]: 'text.primary',
  [ConfidenceLevel.Low]: 'semantic.error',
}

const ConfidenceLabel: React.FC<{ level: ConfidenceLevel; text: string; prefix: string }> = ({
  level,
  text,
  prefix,
}) => (
  <>
    {prefix} ·{' '}
    <Box component="span" color={confidenceColor[level]} fontWeight={500}>
      {text}
    </Box>
  </>
)

const SourceFavicons: React.FC<{ urls: string[] }> = ({ urls }) => (
  <AvatarGroup max={3} spacing="small" sx={{ '& .MuiAvatar-root': { width: 18, height: 18, fontSize: 10 } }}>
    {urls.map(url => (
      <Avatar key={url} src={getFaviconUrl(url)} alt="" />
    ))}
  </AvatarGroup>
)

const AnswerSourcesPanel: React.FC<AnswerSourcesPanelProps> = ({ scrapedAnswer }) => {
  const { formatMessage } = useIntl()
  const searchResults = scrapedAnswer?.searchResults ?? []
  const answerBackgroundSummary = scrapedAnswer?.answerBackgroundSummary
  const confidenceLevel = scrapedAnswer?.confidenceLevel
  const hasSources = searchResults.length > 0
  const [selectedTab, setSelectedTab] = useState<AnswerPanelTabType>(
    answerBackgroundSummary ? AnswerPanelTabType.AISummary : AnswerPanelTabType.Sources,
  )

  if (!scrapedAnswer || (!hasSources && !answerBackgroundSummary && !confidenceLevel)) {
    return <></>
  }

  const uniqueUrls = [...new Set(searchResults.map(result => result.url))]
  const tabs: AnswerPanelTab<AnswerPanelTabType>[] = [
    ...insertIf(Boolean(answerBackgroundSummary), {
      value: AnswerPanelTabType.AISummary,
      label: formatMessage({ id: 'aiScreening.answerPanel.aiSummary' }),
    }),
    ...insertIf(hasSources, {
      value: AnswerPanelTabType.Sources,
      label: `${formatMessage({ id: 'aiScreening.answerPanel.sources' })} · ${searchResults.length}`,
      icon: <SourceFavicons urls={uniqueUrls} />,
    }),
    ...insertIf(Boolean(confidenceLevel), {
      value: AnswerPanelTabType.Confidence,
      label: confidenceLevel && (
        <ConfidenceLabel
          level={confidenceLevel}
          prefix={formatMessage({ id: 'aiScreening.answerPanel.confidence' })}
          text={formatMessage({ id: `aiScreening.evidence.confidence.${confidenceLevel}` })}
        />
      ),
      tooltip: formatMessage({ id: `aiScreening.evidence.confidenceDescription.${confidenceLevel}` }),
      disabled: !hasEvidenceDetails(scrapedAnswer),
    }),
  ]
  const selectableTabs = tabs.filter(tab => !tab.disabled)
  const activeTab = selectableTabs.some(tab => tab.value === selectedTab) ? selectedTab : selectableTabs[0]?.value

  return (
    <Stack px={2} pt={2} pb={2.5} bgcolor="grey.100" borderTop={1} borderColor="grey.300">
      <AnswerPanelTabs value={activeTab} tabs={tabs} onChange={setSelectedTab} />
      {activeTab && <Divider sx={{ mt: 2, mb: 3 }} />}
      {activeTab === AnswerPanelTabType.AISummary && answerBackgroundSummary && (
        <AISummaryContent
          text={answerBackgroundSummary}
          searchResults={searchResults}
          onSwitchToSources={() => setSelectedTab(AnswerPanelTabType.Sources)}
        />
      )}
      {activeTab === AnswerPanelTabType.Sources && <AnswerSourcesList searchResults={searchResults} />}
      {activeTab === AnswerPanelTabType.Confidence && <AnswerEvidenceDetails scrapedAnswer={scrapedAnswer} />}
    </Stack>
  )
}

export default AnswerSourcesPanel
